import React, {useRef, useEffect, useState, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import useSearchStore from '../../store/useSearchStore';
import {
  Container,
  SearchSection,
  InputWrapper,
  SearchInput,
  SearchInputContainer,
  SearchButton,
  AdvancedButton,
  ClearButton,
  BulkButton,
  HelperText,
  LoadingSpinner,
  DimensionSuggestion,
  ToggleButton,
  VehicleSearchBadge,
} from './SearchTopBar.styled';
import SearchSuggestions from './components/SearchSuggestions';
import BulkSearchModal from './components/BulkSearchModal';
import AdvancedSearchBuilder from './components/AdvancedSearchBuilder';
import ActiveFiltersIndicator from './components/ActiveFiltersIndicator';
import {useClickOutside} from '../../hooks';
import { listTyresApi as getTyresApi } from '../../api/endpoints';
import debounce from '../../lib/debounce';
import {suggestDimensionFormat} from '../../lib/parseTyreDimensions';

const MIN_SEARCH_LENGTH = 2;
const SUGGESTIONS_LIMIT = 8;

const SearchTopBar = () => {
  const {t} = useTranslation();
  // Get state and actions from store
  const {
    searchValue,
    vehicleSearch,
    loading: isLoading,
    seasonFilters,
    brandFilters,
    minPrice,
    maxPrice,
    searchOnlyInStock,
    suggestionsEnabled,
    recentSearchesEnabled,
    setSearchValue,
    setVehicleSearch,
    clearVehicleSearch,
    addVehicleSearch,
    setSeasonFilter,
    setBrandFilter,
    setMinPrice,
    setMaxPrice,
    setSearchOnlyInStock,
    setSuggestionsEnabled,
    setRecentSearchesEnabled,
    addRecentSearch,
    fetchTyres,
    resetFilters,
  } = useSearchStore();

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debouncedFetchSuggestionsRef = useRef(null);
  const wasInputFocusedRef = useRef(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [dimensionSuggestion, setDimensionSuggestion] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showAdvancedBuilder, setShowAdvancedBuilder] = useState(false);

  const handleClear = useCallback(() => {
    if (vehicleSearch) {
      clearVehicleSearch();
    } else {
      setSearchValue('');
    }
    inputRef.current?.focus();
    queueMicrotask(() => fetchTyres({refresh: true}));
  }, [vehicleSearch, clearVehicleSearch, setSearchValue, fetchTyres]);

  // Toggle handlers for preferences
  const handleToggleSuggestions = useCallback(() => {
    const newValue = !suggestionsEnabled;
    setSuggestionsEnabled(newValue);
    if (!newValue) {
      setShowSuggestions(false);
      setSuggestions([]);
    }
  }, [suggestionsEnabled, setSuggestionsEnabled]);

  const handleToggleRecentSearches = useCallback(() => {
    setRecentSearchesEnabled(!recentSearchesEnabled);
  }, [recentSearchesEnabled, setRecentSearchesEnabled]);

  // Fetch suggestions
  const fetchSuggestions = async (query) => {
    if (query.length < MIN_SEARCH_LENGTH) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setSuggestionsLoading(true);
    setShowSuggestions(true);

    try {
      const searchQuery = {
        limit: SUGGESTIONS_LIMIT,
        available: true,
        offset: 0,
        populate: 'brand, season',
        search: `catalogNumber__icontains:${query.trim()}||dimensions__icontains:${query.trim()}`,
      };

      const results = await getTyresApi(searchQuery);
      setSuggestions(results || []);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  // Check for dimension suggestions
  useEffect(() => {
    const suggestion = suggestDimensionFormat(searchValue);
    setDimensionSuggestion(suggestion);
  }, [searchValue]);

  // Debounced suggestions fetch
  useEffect(() => {
    if (suggestionsEnabled && searchValue.length >= MIN_SEARCH_LENGTH) {
      if (!debouncedFetchSuggestionsRef.current) {
        debouncedFetchSuggestionsRef.current = debounce(fetchSuggestions, 1000);
      }
      debouncedFetchSuggestionsRef.current(searchValue);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }

    return () => {
      if (debouncedFetchSuggestionsRef.current) {
        debouncedFetchSuggestionsRef.current.cancel?.();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, suggestionsEnabled]);

  // Close dropdown when clicking outside
  useClickOutside(dropdownRef, () => {
    setShowSuggestions(false);
  });

  // ESC key handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (searchValue) {
          handleClear();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchValue, handleClear]);

  const handleFocus = () => {
    wasInputFocusedRef.current = true;
  };

  const handleBlur = () => {
    wasInputFocusedRef.current = false;
  };

  // Restore focus after loading completes
  useEffect(() => {
    if (!isLoading && wasInputFocusedRef.current && inputRef.current) {
      const timer = setTimeout(() => {
        if (inputRef.current && wasInputFocusedRef.current) {
          inputRef.current.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  const handleSelectSuggestion = useCallback(
    (tyre) => {
      const searchTerm = tyre.catalogNumber;
      setSearchValue(searchTerm);
      setShowSuggestions(false);
      setTimeout(() => {
        addRecentSearch(searchTerm);
        fetchTyres({refresh: true});
      }, 0);
    },
    [setSearchValue, addRecentSearch, fetchTyres]
  );

  const handleApplyDimensionSuggestion = useCallback(() => {
    if (dimensionSuggestion) {
      setSearchValue(dimensionSuggestion.suggested);
      setDimensionSuggestion(null);
    }
  }, [dimensionSuggestion, setSearchValue]);

  const handleBulkSearch = useCallback(
    (eans, vehicleInfo) => {
      const combinedSearch = eans.join('||');
      setSearchValue(combinedSearch);
      setVehicleSearch(vehicleInfo);
      setTimeout(() => {
        addVehicleSearch(eans, vehicleInfo);
        fetchTyres({refresh: true});
      }, 0);
    },
    [setSearchValue, setVehicleSearch, addVehicleSearch, fetchTyres]
  );

  const handleSearchSubmit = useCallback(() => {
    if (searchValue.length >= MIN_SEARCH_LENGTH || searchValue.length === 0) {
      if (searchValue.trim()) {
        addRecentSearch(searchValue.trim());
      }
      fetchTyres({refresh: true});
    }
  }, [searchValue, addRecentSearch, fetchTyres]);

  const handleAdvancedSearch = useCallback(
    (query) => {
      let searchTerm = query.catalogNumber || '';

      if (query.width || query.profile || query.rim) {
        const dims = [query.width, query.profile, query.rim]
          .filter(Boolean)
          .join('');
        searchTerm = searchTerm ? `${searchTerm} ${dims}` : dims;
      }

      const eans = query.resolvedEans || [];
      if (eans.length > 0) {
        const vehicleInfo = {
          vendor: query.vehicleVendor,
          model: query.vehicleModel,
          year: query.vehicleYear,
          version: query.vehicleVersion,
          fitment: query.vehicleFitment,
          eanCount: eans.length,
        };
        searchTerm = eans.join('||');
        setVehicleSearch(vehicleInfo);
        addVehicleSearch(eans, vehicleInfo);
      } else {
        setVehicleSearch(null);
      }

      setSearchValue(searchTerm);

      seasonFilters.forEach((s) => {
        const shouldBeSelected = query.selectedSeasons.includes(s.id);
        if (s.selected !== shouldBeSelected) {
          setSeasonFilter(s.id, shouldBeSelected);
        }
      });

      brandFilters.forEach((b) => {
        const shouldBeSelected = query.selectedBrands.includes(b.value);
        if (b.selected !== shouldBeSelected) {
          setBrandFilter(b.value, shouldBeSelected);
        }
      });

      setMinPrice(query.minPrice || '');
      setMaxPrice(query.maxPrice || '');
      setSearchOnlyInStock(query.inStockOnly);

      queueMicrotask(() => fetchTyres({refresh: true}));
    },
    [
      setSearchValue,
      setVehicleSearch,
      addVehicleSearch,
      setSeasonFilter,
      setBrandFilter,
      setMinPrice,
      setMaxPrice,
      setSearchOnlyInStock,
      fetchTyres,
      seasonFilters,
      brandFilters,
    ]
  );

  const isSearchDisabled =
    !vehicleSearch &&
    searchValue.length < MIN_SEARCH_LENGTH &&
    searchValue.length > 0;
  const showHelperText =
    !vehicleSearch &&
    searchValue.length > 0 &&
    searchValue.length < MIN_SEARCH_LENGTH;

  return (
    <Container>
      <SearchSection>
        <SearchInputContainer ref={dropdownRef}>
          {vehicleSearch ? (
            <VehicleSearchBadge>
              <div className="vehicle-icon">
                <i className="mdi mdi-car" />
              </div>
              <div className="vehicle-info">
                <span className="vehicle-title">
                  {vehicleSearch.vendor} {vehicleSearch.model}
                </span>
                <span className="vehicle-details">
                  {vehicleSearch.year} • {vehicleSearch.version} •{' '}
                  {vehicleSearch.fitment}
                </span>
              </div>
              <span className="vehicle-count">
                {vehicleSearch.eanCount} {t('products') || 'products'}
              </span>
              <ClearButton
                onClick={handleClear}
                type="button"
                title={t('clearSearch')}
              >
                <i className="mdi mdi-close-circle" />
              </ClearButton>
            </VehicleSearchBadge>
          ) : (
            <InputWrapper>
              <i className="mdi mdi-magnify" />
              <SearchInput
                ref={inputRef}
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyPress={(e) =>
                  e.key === 'Enter' &&
                  !isSearchDisabled &&
                  !isLoading &&
                  handleSearchSubmit()
                }
                disabled={isLoading}
                autoComplete="off"
              />
              <ToggleButton
                onClick={handleToggleRecentSearches}
                enabled={recentSearchesEnabled}
                type="button"
                title={
                  recentSearchesEnabled
                    ? t('disableRecentSearches')
                    : t('enableRecentSearches')
                }
              >
                <i className="mdi mdi-history" />
              </ToggleButton>
              <ToggleButton
                onClick={handleToggleSuggestions}
                enabled={suggestionsEnabled}
                type="button"
                title={
                  suggestionsEnabled
                    ? t('disableSuggestions')
                    : t('enableSuggestions')
                }
              >
                <i className="mdi mdi-lightbulb-on" />
              </ToggleButton>
              <BulkButton
                onClick={() => setShowBulkModal(true)}
                type="button"
                title={t('searchByVehicle')}
              >
                <i className="mdi mdi-car" />
              </BulkButton>
              {searchValue && !isLoading && (
                <ClearButton onClick={handleClear} type="button">
                  <i className="mdi mdi-close-circle" />
                </ClearButton>
              )}
            </InputWrapper>
          )}
          {showHelperText && (
            <HelperText error>
              <i className="mdi mdi-information-outline" />
              {t('minCharsRequired', {count: MIN_SEARCH_LENGTH})}
            </HelperText>
          )}
          {!vehicleSearch && dimensionSuggestion && !showSuggestions && (
            <DimensionSuggestion onClick={handleApplyDimensionSuggestion}>
              <i className="mdi mdi-lightbulb-on-outline" />
              <span>
                {t('didYouMean')}:{' '}
                <strong>{dimensionSuggestion.suggested}</strong>?
              </span>
            </DimensionSuggestion>
          )}
          {!vehicleSearch && suggestionsEnabled && (
            <SearchSuggestions
              isVisible={
                showSuggestions && searchValue.length >= MIN_SEARCH_LENGTH
              }
              suggestions={suggestions}
              searchQuery={searchValue}
              onSelectSuggestion={handleSelectSuggestion}
              isLoading={suggestionsLoading}
            />
          )}
        </SearchInputContainer>

        <SearchButton
          onClick={handleSearchSubmit}
          disabled={isSearchDisabled || isLoading}
        >
          {isLoading ? (
            <LoadingSpinner className="mdi mdi-loading" />
          ) : (
            <i className="mdi mdi-magnify" />
          )}
          {t('search')}
        </SearchButton>

        <AdvancedButton onClick={() => setShowAdvancedBuilder(true)}>
          <i className="mdi mdi-tune" />
          {t('advanced')}
        </AdvancedButton>
      </SearchSection>

      <BulkSearchModal
        isVisible={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onSearch={handleBulkSearch}
      />

      <AdvancedSearchBuilder
        isVisible={showAdvancedBuilder}
        onClose={() => setShowAdvancedBuilder(false)}
        onSearch={handleAdvancedSearch}
        availableSeasons={seasonFilters || []}
        availableBrands={brandFilters || []}
      />

      <ActiveFiltersIndicator
        seasonFilters={seasonFilters || []}
        brandFilters={brandFilters || []}
        minPrice={minPrice}
        maxPrice={maxPrice}
        searchOnlyInStock={searchOnlyInStock}
        onRemoveSeason={(id) => {
          setSeasonFilter(id, false);
          queueMicrotask(() => fetchTyres({refresh: true}));
        }}
        onRemoveBrand={(id) => {
          setBrandFilter(id, false);
          queueMicrotask(() => fetchTyres({refresh: true}));
        }}
        onClearPrice={() => {
          setMinPrice('');
          setMaxPrice('');
          queueMicrotask(() => fetchTyres({refresh: true}));
        }}
        onClearInStock={() => {
          setSearchOnlyInStock(false);
          queueMicrotask(() => fetchTyres({refresh: true}));
        }}
        onClearAll={() => {
          resetFilters();
          queueMicrotask(() => fetchTyres({refresh: true}));
        }}
      />
    </Container>
  );
};

export default SearchTopBar;
